import React, { useState, useEffect } from 'react';
import { Building2, Phone, Mail, Globe, FileText, Truck, MapPin, FileCheck, Save, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Page size configurations
const PAGE_SIZES = {
  'A4': { name: 'A4 (210mm × 297mm)', size: 'A4', width: '210mm', height: '297mm', maxItems: 15 },
  'A3': { name: 'A3 (297mm × 420mm)', size: 'A3', width: '297mm', height: '420mm', maxItems: 25 },
  'POS': { name: 'POS (80mm × 200mm)', size: 'custom', width: '80mm', height: '200mm', maxItems: 8 }
};

export const PrintChallan = ({ transactions = [], products = [], warehouse, onClose, onSave }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedFromWarehouse, setSelectedFromWarehouse] = useState(null);
  const [pageSize, setPageSize] = useState('A4');
  const [toDetails, setToDetails] = useState({
    name: '',
    address: '',
    phone: '',
    gst: ''
  });
  const [transportDetails, setTransportDetails] = useState({
    vehicleNo: '',
    driverName: '',
    driverPhone: ''
  });
  // Item rates - allow editing per item
  const [itemRates, setItemRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedChallanNumber, setSavedChallanNumber] = useState(null);
  const [challanNo, setChallanNo] = useState(`CH-${Date.now().toString().slice(-8)}`);
  const [remarks, setRemarks] = useState('');

  // Validation: Check if all transactions belong to same vendor
  const uniqueVendors = [...new Set(transactions.map(t => t.vendor_name))];
  const hasMultipleVendors = uniqueVendors.length > 1;

  // Initialize item rates
  useEffect(() => {
    const initialRates = {};
    transactions.forEach(trans => {
      initialRates[trans.id] = trans.rate || 0;
    });
    setItemRates(initialRates);
  }, [transactions]);

  // Check if challan is already saved for these transactions
  useEffect(() => {
    const checkIfSaved = async () => {
      if (transactions.length > 0) {
        try {
          const transactionIds = transactions.map(t => t.id).join(',');
          const response = await axios.get(`${API}/api/challans/check-saved?transaction_ids=${transactionIds}`, { withCredentials: true });
          if (response.data.saved) {
            setIsSaved(true);
            setSavedChallanNumber(response.data.challan_number);
            setChallanNo(response.data.challan_number);
          }
        } catch (error) {
          console.error("Error checking challan status:", error);
        }
      }
    };
    checkIfSaved();
  }, [transactions]);

  // Fetch warehouses on mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/api/admin/warehouses/list`, { withCredentials: true });
        if (response.data && response.data.length > 0) {
          setWarehouses(response.data);
          const currentWh = response.data.find(w => w.warehouse_name === warehouse);
          if (currentWh) {
            setSelectedFromWarehouse(currentWh);
          } else {
            setSelectedFromWarehouse(response.data[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        const defaultWh = {
          id: 'default',
          warehouse_name: warehouse || 'Main Warehouse',
          location: 'Amritsar, Punjab',
          address: `${warehouse || 'Main Warehouse'}, Amritsar, Punjab`
        };
        setWarehouses([defaultWh]);
        setSelectedFromWarehouse(defaultWh);
      } finally {
        setLoading(false);
      }
    };
    fetchWarehouses();
  }, [warehouse]);

  // Auto-fill transport details and customer from first transaction if available
  useEffect(() => {
    if (transactions.length > 0 && transactions[0]) {
      const firstTrans = transactions[0];
      setTransportDetails({
        vehicleNo: firstTrans.vehicle_number || '',
        driverName: firstTrans.driver_name || '',
        driverPhone: firstTrans.driver_contact || ''
      });
      // Auto-fill vendor name as customer name
      if (!toDetails.name && firstTrans.vendor_name) {
        setToDetails(prev => ({
          ...prev,
          name: firstTrans.vendor_name
        }));
      }
    }
  }, [transactions]);

  const handlePrint = () => {
    if (hasMultipleVendors) {
      toast.error("Cannot print challan for transactions from different vendors. Please select transactions from the same vendor.");
      return;
    }
    window.print();
  };

  const handleSaveChallan = async () => {
    if (isSaved) {
      toast.info(`Challan already saved as ${savedChallanNumber}`);
      return;
    }

    if (hasMultipleVendors) {
      toast.error("Cannot save challan for transactions from different vendors.");
      return;
    }

    if (!toDetails.name.trim()) {
      toast.error("Please enter receiver/party name before saving");
      return;
    }

    setSaving(true);
    try {
      // Build items list with product details and rates
      const items = limitedTransactions.map(trans => {
        const product = getProductForTransaction(trans);
        const rate = itemRates[trans.id] || 0;
        return {
          product_id: trans.product_id,
          product_name: trans.product_name,
          category: product.category || '',
          quantity: trans.quantity,
          rate: rate,
          amount: trans.quantity * rate,
          unit: 'kg',
          sku: product.sku || '',
          transaction_id: trans.id
        };
      });

      const challanData = {
        from_warehouse: selectedFromWarehouse?.warehouse_name || warehouse,
        from_location: selectedFromWarehouse?.location || '',
        to_name: toDetails.name,
        to_address: toDetails.address,
        to_phone: toDetails.phone,
        to_gst: toDetails.gst,
        vehicle_number: transportDetails.vehicleNo,
        driver_name: transportDetails.driverName,
        driver_phone: transportDetails.driverPhone,
        items: items,
        transaction_ids: transactions.map(t => t.id),
        page_size: pageSize,
        notes: remarks,
        total_quantity: totalQuantity,
        total_amount: totalAmount
      };

      const response = await axios.post(`${API}/api/challans/save`, challanData, { withCredentials: true });
      
      setIsSaved(true);
      setSavedChallanNumber(response.data.challan_number);
      setChallanNo(response.data.challan_number);
      toast.success(`Challan saved successfully: ${response.data.challan_number}`);
      
      if (onSave) {
        onSave(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save challan");
    } finally {
      setSaving(false);
    }
  };

  const currentDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  // Calculate totals
  const totalQuantity = transactions.reduce((sum, t) => sum + (t.quantity || 0), 0);
  const totalAmount = transactions.reduce((sum, t) => sum + ((t.quantity || 0) * (itemRates[t.id] || 0)), 0);

  // Get product details for each transaction
  const getProductForTransaction = (trans) => {
    return products.find(p => p.id === trans.product_id) || {};
  };

  // Limit items based on page size for single-page printing
  const selectedPageConfig = PAGE_SIZES[pageSize];
  const limitedTransactions = transactions.slice(0, selectedPageConfig.maxItems);
  const hasMoreItems = transactions.length > selectedPageConfig.maxItems;

  // Get CSS for page size
  const getPageSizeCSS = () => {
    const config = selectedPageConfig;
    if (pageSize === 'POS') {
      return `
        @page {
          size: ${config.width} auto;
          margin: 3mm;
        }
      `;
    }
    return `
      @page {
        size: ${config.size};
        margin: 8mm;
      }
    `;
  };

  // Update rate for specific item
  const updateItemRate = (transId, rate) => {
    setItemRates(prev => ({
      ...prev,
      [transId]: parseFloat(rate) || 0
    }));
  };

  return (
    <div className="print-challan-container">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-challan-container, .print-challan-container * {
            visibility: visible;
          }
          .print-challan-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .no-print {
            display: none !important;
          }
          .challan-content {
            padding: ${pageSize === 'POS' ? '8px' : '15px'};
          }
          ${getPageSizeCSS()}
          /* Single page - no page breaks */
          .challan-content {
            page-break-after: avoid;
            page-break-before: avoid;
            page-break-inside: avoid;
          }
          /* Ensure everything fits on one page */
          table {
            page-break-inside: avoid;
          }
          tr {
            page-break-inside: avoid;
          }
          .signature-section {
            page-break-inside: avoid;
          }
          /* POS specific styles */
          ${pageSize === 'POS' ? `
            .challan-content {
              font-size: 10px;
              padding: 5px;
            }
            .challan-content h1 {
              font-size: 14px !important;
            }
            .challan-content h2 {
              font-size: 12px !important;
            }
            .challan-content table {
              font-size: 9px;
            }
            .challan-content .signature-section {
              display: none;
            }
            .challan-content .terms-section {
              display: none;
            }
            .letterhead-header {
              display: none;
            }
            .pos-header {
              display: block !important;
            }
          ` : `
            .pos-header {
              display: none !important;
            }
          `}
        }
        @media screen {
          .challan-content {
            max-width: 900px;
            margin: 0 auto;
          }
          .pos-header {
            display: none;
          }
        }
      `}</style>

      {/* Input Form - Only shown on screen */}
      <div className="no-print mb-6 space-y-4">
        {/* Multiple Vendors Warning */}
        {hasMultipleVendors && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">
              Warning: Selected transactions belong to different vendors ({uniqueVendors.join(', ')}). 
              Challan can only be generated for same vendor transactions.
            </span>
          </div>
        )}

        {/* Saved Status Banner */}
        {isSaved && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">
              Challan saved as: <span className="font-mono">{savedChallanNumber}</span>
            </span>
          </div>
        )}

        {/* Selected Items Summary */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 font-medium">
            <FileText className="w-4 h-4 inline mr-2" />
            {transactions.length} product(s) selected | Total Qty: {totalQuantity.toFixed(2)} kg
            {totalAmount > 0 && ` | Total Amount: ₹${totalAmount.toFixed(2)}`}
          </p>
        </div>

        {/* Page Size Selection */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            Print Settings
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Paper Size *</Label>
              <Select value={pageSize} onValueChange={setPageSize}>
                <SelectTrigger data-testid="page-size-select">
                  <SelectValue placeholder="Select paper size" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PAGE_SIZES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <p className="text-sm text-blue-600">
                Max items for {pageSize}: {selectedPageConfig.maxItems}
                {hasMoreItems && (
                  <span className="text-amber-600 ml-2">
                    ({transactions.length - selectedPageConfig.maxItems} items will be excluded)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* FROM Warehouse Selection */}
        <div className="p-4 bg-lime-50 border border-lime-200 rounded-lg">
          <h4 className="font-bold text-lime-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            FROM (Dispatch Warehouse)
          </h4>
          <div className="space-y-2">
            <Label>Select Warehouse *</Label>
            {loading ? (
              <div className="p-2 border rounded bg-stone-50 text-stone-500">Loading warehouses...</div>
            ) : warehouses.length === 0 ? (
              <div className="p-2 border rounded bg-amber-50 text-amber-700">No warehouses found</div>
            ) : (
              <Select 
                value={selectedFromWarehouse?.id || ''} 
                onValueChange={(value) => {
                  const wh = warehouses.find(w => w.id === value);
                  setSelectedFromWarehouse(wh);
                }}
              >
                <SelectTrigger data-testid="from-warehouse-select">
                  <SelectValue placeholder="Select dispatch warehouse">
                    {selectedFromWarehouse ? `${selectedFromWarehouse.warehouse_name} - ${selectedFromWarehouse.location}` : "Select dispatch warehouse"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.id} value={wh.id}>
                      {wh.warehouse_name} - {wh.location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedFromWarehouse && (
              <p className="text-sm text-lime-700 mt-2">
                <MapPin className="w-3 h-3 inline mr-1" />
                {selectedFromWarehouse.location}
              </p>
            )}
          </div>
        </div>

        {/* TO Details - Manual Entry */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5" />
            TO (Receiver Details)
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Customer/Party Name *</Label>
              <Input
                value={toDetails.name}
                onChange={(e) => setToDetails({...toDetails, name: e.target.value})}
                placeholder="Enter receiver name"
                data-testid="to-name-input"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                value={toDetails.phone}
                onChange={(e) => setToDetails({...toDetails, phone: e.target.value})}
                placeholder="Enter phone number"
                data-testid="to-phone-input"
              />
            </div>
            <div className="col-span-2">
              <Label>Delivery Address</Label>
              <Input
                value={toDetails.address}
                onChange={(e) => setToDetails({...toDetails, address: e.target.value})}
                placeholder="Enter complete delivery address"
                data-testid="to-address-input"
              />
            </div>
            <div>
              <Label>GSTIN (Optional)</Label>
              <Input
                value={toDetails.gst}
                onChange={(e) => setToDetails({...toDetails, gst: e.target.value})}
                placeholder="Enter GST number"
                data-testid="to-gst-input"
              />
            </div>
          </div>
        </div>

        {/* Transport Details */}
        <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg">
          <h4 className="font-bold text-stone-700 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Transport Details
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Vehicle Number</Label>
              <Input
                value={transportDetails.vehicleNo}
                onChange={(e) => setTransportDetails({...transportDetails, vehicleNo: e.target.value})}
                placeholder="e.g., PB-02-AB-1234"
                data-testid="vehicle-no-input"
              />
            </div>
            <div>
              <Label>Driver Name</Label>
              <Input
                value={transportDetails.driverName}
                onChange={(e) => setTransportDetails({...transportDetails, driverName: e.target.value})}
                placeholder="Enter driver name"
                data-testid="driver-name-challan-input"
              />
            </div>
            <div>
              <Label>Driver Phone</Label>
              <Input
                value={transportDetails.driverPhone}
                onChange={(e) => setTransportDetails({...transportDetails, driverPhone: e.target.value})}
                placeholder="Driver contact"
                data-testid="driver-phone-input"
              />
            </div>
          </div>
        </div>

        {/* Item Rates Section */}
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <h4 className="font-bold text-purple-800 mb-4">Item Rates (Optional - for Amount calculation)</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {limitedTransactions.map((trans, index) => (
              <div key={trans.id} className="flex items-center gap-4 p-2 bg-white rounded border">
                <span className="text-sm font-medium flex-1">{index + 1}. {trans.product_name}</span>
                <span className="text-sm text-stone-500">{trans.quantity.toFixed(2)} kg</span>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">₹/kg</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={itemRates[trans.id] || ''}
                    onChange={(e) => updateItemRate(trans.id, e.target.value)}
                    placeholder="0.00"
                    className="w-24 h-8 text-sm"
                    data-testid={`rate-input-${trans.id}`}
                  />
                </div>
                <span className="text-sm font-medium text-purple-700 w-24 text-right">
                  ₹{((trans.quantity || 0) * (itemRates[trans.id] || 0)).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Remarks */}
        <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg">
          <Label>Remarks / Notes</Label>
          <Input
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Any additional notes for the challan"
            data-testid="challan-remarks-input"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 flex-wrap">
          <Button 
            onClick={handlePrint}
            className="bg-lime-700 text-white hover:bg-lime-800 flex items-center gap-2"
            disabled={!selectedFromWarehouse || hasMultipleVendors}
            data-testid="print-challan-btn"
          >
            <FileText className="w-4 h-4" />
            Print Challan ({pageSize})
          </Button>
          
          <Button
            onClick={handleSaveChallan}
            variant={isSaved ? "outline" : "default"}
            className={isSaved ? "border-green-500 text-green-700" : "bg-blue-600 hover:bg-blue-700 text-white"}
            disabled={saving || !toDetails.name.trim() || hasMultipleVendors}
            data-testid="save-challan-btn"
          >
            {isSaved ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Saved
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Challan'}
              </>
            )}
          </Button>
          
          {onClose && (
            <Button variant="outline" onClick={onClose} data-testid="cancel-challan-btn">
              Cancel
            </Button>
          )}
        </div>
        
        {hasMoreItems && (
          <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
            Note: Only the first {selectedPageConfig.maxItems} items will be printed on a single {pageSize} page. 
            {transactions.length - selectedPageConfig.maxItems} item(s) excluded.
          </p>
        )}
        
        {!toDetails.name.trim() && (
          <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
            Fill in receiver/party name to enable "Save Challan" option.
          </p>
        )}
      </div>

      {/* Printable Challan */}
      <div className="challan-content bg-white p-8 border border-stone-200 rounded-lg" style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* POS Header - Compact for thermal printing */}
        <div className="pos-header text-center mb-4" style={{ display: 'none' }}>
          <h1 className="text-base font-bold">S.P SINGH AND SONS</h1>
          <p className="text-xs">1158, Kanak Mandi, Amritsar</p>
          <p className="text-xs">Ph: 9815269088 | GSTIN: 03AOIPS7062J1ZN</p>
          <hr className="my-2 border-dashed" />
          <p className="text-sm font-bold">DELIVERY CHALLAN</p>
          <p className="text-xs">{challanNo} | {currentDate}</p>
          <hr className="my-2 border-dashed" />
        </div>

        {/* Letterhead Header - Standard for A4/A3 */}
        <div className="letterhead-header border-b-4 border-double border-lime-700 pb-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#4d7c0f', fontFamily: 'Georgia, serif', letterSpacing: '1px' }}>
                S.P SINGH AND SONS
              </h1>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                <strong>Regd Office:</strong> 1158, Kanak Mandi<br />
                Near Golden Temple, Amritsar<br />
                (Punjab) India - 143001
              </p>
            </div>
            <div className="text-right">
              <img src="/logo.jpeg" alt="SPS Groups" className="h-20 mb-2 ml-auto" style={{ objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
              <p className="text-lg font-bold" style={{ color: '#4d7c0f' }}>SPS GROUPS</p>
            </div>
          </div>
          
          {/* Contact Row */}
          <div className="flex justify-between mt-4 pt-3 border-t border-stone-200 text-xs text-stone-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                +91 9815269088, +91 9463564199
              </span>
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                spsinghandsons@gmail.com
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                www.amanbrand.in
              </span>
              <span><strong>GSTIN:</strong> 03AOIPS7062J1ZN</span>
            </div>
          </div>
        </div>

        {/* Challan Title */}
        <div className="text-center mb-6 py-4 bg-amber-50 rounded-lg border-2 border-amber-300">
          <h2 className="text-3xl font-bold text-amber-800 tracking-wide">DELIVERY CHALLAN</h2>
          <p className="text-sm text-amber-600 mt-1">(For Goods Dispatch - {transactions.length} Item{transactions.length > 1 ? 's' : ''})</p>
        </div>

        {/* Challan Details Row */}
        <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
          <div className="p-3 bg-stone-50 rounded border border-stone-200">
            <p className="text-stone-500 text-xs">Challan No.</p>
            <p className="font-bold text-lg text-stone-800">{challanNo}</p>
          </div>
          <div className="p-3 bg-stone-50 rounded border border-stone-200">
            <p className="text-stone-500 text-xs">Date</p>
            <p className="font-bold text-lg text-stone-800">{currentDate}</p>
          </div>
          <div className="p-3 bg-stone-50 rounded border border-stone-200">
            <p className="text-stone-500 text-xs">Time</p>
            <p className="font-bold text-lg text-stone-800">{new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>

        {/* FROM and TO Section */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* FROM Section */}
          <div className="p-4 border-2 border-lime-300 rounded-lg bg-lime-50">
            <h3 className="font-bold text-lime-800 mb-3 pb-2 border-b border-lime-200 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              FROM (Dispatch)
            </h3>
            <div className="text-sm space-y-1">
              <p className="font-semibold text-stone-800 text-lg">
                {selectedFromWarehouse?.warehouse_name || warehouse}
              </p>
              <p className="text-stone-600">
                {selectedFromWarehouse?.location || 'Amritsar, Punjab'}
              </p>
              <p className="text-stone-600">S.P Singh and Sons</p>
              <p className="text-stone-500 text-xs mt-2">GSTIN: 03AOIPS7062J1ZN</p>
            </div>
          </div>

          {/* TO Section */}
          <div className="p-4 border-2 border-amber-300 rounded-lg bg-amber-50">
            <h3 className="font-bold text-amber-800 mb-3 pb-2 border-b border-amber-200 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              TO (Receiver)
            </h3>
            <div className="text-sm space-y-2">
              <p className="font-semibold text-stone-800 text-lg">
                {toDetails.name || '_______________________'}
              </p>
              <p className="text-stone-600">
                {toDetails.address || '_______________________'}
              </p>
              <p className="text-stone-600">
                Ph: {toDetails.phone || '_______________________'}
              </p>
              {toDetails.gst && (
                <p className="text-stone-500 text-xs mt-2">GSTIN: {toDetails.gst}</p>
              )}
            </div>
          </div>
        </div>

        {/* Transport Details */}
        <div className="mb-6 p-3 border border-stone-300 rounded-lg bg-stone-50">
          <h3 className="font-bold text-stone-700 mb-2 text-sm">TRANSPORT DETAILS</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-stone-500">Vehicle No.:</span>
              <span className="font-semibold ml-2">{transportDetails.vehicleNo || '_____________'}</span>
            </div>
            <div>
              <span className="text-stone-500">Driver:</span>
              <span className="font-semibold ml-2">{transportDetails.driverName || '_____________'}</span>
            </div>
            <div>
              <span className="text-stone-500">Driver Ph.:</span>
              <span className="font-semibold ml-2">{transportDetails.driverPhone || '_____________'}</span>
            </div>
          </div>
        </div>

        {/* Item Details Table - Enhanced with Rate and Amount */}
        <div className="mb-6">
          <h3 className="font-bold text-stone-700 mb-3">ITEM DETAILS</h3>
          <table className="w-full border-collapse" style={{ fontSize: '11px' }}>
            <thead>
              <tr style={{ backgroundColor: '#4d7c0f', color: 'white' }}>
                <th className="text-left p-2 border border-stone-400" style={{ width: '40px' }}>Sr.</th>
                <th className="text-left p-2 border border-stone-400">Product Name</th>
                <th className="text-left p-2 border border-stone-400" style={{ width: '80px' }}>SKU</th>
                <th className="text-center p-2 border border-stone-400" style={{ width: '70px' }}>Qty</th>
                <th className="text-center p-2 border border-stone-400" style={{ width: '50px' }}>Unit</th>
                <th className="text-right p-2 border border-stone-400" style={{ width: '80px' }}>Rate (₹)</th>
                <th className="text-right p-2 border border-stone-400" style={{ width: '90px' }}>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {limitedTransactions.map((trans, index) => {
                const product = getProductForTransaction(trans);
                const rate = itemRates[trans.id] || 0;
                const amount = (trans.quantity || 0) * rate;
                return (
                  <tr key={trans.id || index}>
                    <td className="p-2 border border-stone-300 text-center">{index + 1}</td>
                    <td className="p-2 border border-stone-300 font-semibold">
                      {trans.product_name || '—'}
                      {product.category && <span className="text-xs text-stone-500 block">{product.category}</span>}
                    </td>
                    <td className="p-2 border border-stone-300 text-stone-500">{product.sku || '—'}</td>
                    <td className="p-2 border border-stone-300 text-center font-bold">{trans.quantity?.toFixed(2) || '—'}</td>
                    <td className="p-2 border border-stone-300 text-center">kg</td>
                    <td className="p-2 border border-stone-300 text-right">{rate > 0 ? rate.toFixed(2) : '—'}</td>
                    <td className="p-2 border border-stone-300 text-right font-medium">{amount > 0 ? amount.toFixed(2) : '—'}</td>
                  </tr>
                );
              })}
              {/* Add empty rows if less than 3 items (only for A4/A3) */}
              {pageSize !== 'POS' && limitedTransactions.length < 3 && Array.from({ length: 3 - limitedTransactions.length }).map((_, i) => (
                <tr key={`empty-${i}`}>
                  <td className="p-2 border border-stone-300 text-center">{limitedTransactions.length + i + 1}</td>
                  <td className="p-2 border border-stone-300"></td>
                  <td className="p-2 border border-stone-300"></td>
                  <td className="p-2 border border-stone-300"></td>
                  <td className="p-2 border border-stone-300"></td>
                  <td className="p-2 border border-stone-300"></td>
                  <td className="p-2 border border-stone-300"></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#fef3c7' }}>
                <td colSpan={3} className="p-2 border border-stone-400 text-right font-bold">
                  TOTAL:
                </td>
                <td className="p-2 border border-stone-400 text-center font-bold text-lg">
                  {totalQuantity.toFixed(2)}
                </td>
                <td className="p-2 border border-stone-400 text-center font-bold">kg</td>
                <td className="p-2 border border-stone-400"></td>
                <td className="p-2 border border-stone-400 text-right font-bold text-lg">
                  {totalAmount > 0 ? `₹${totalAmount.toFixed(2)}` : '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Remarks */}
        {remarks && (
          <div className="mb-4 p-3 bg-stone-50 rounded border border-stone-200">
            <p className="text-sm"><strong>Remarks:</strong> {remarks}</p>
          </div>
        )}

        {/* Terms & Conditions */}
        <div className="mb-6 p-3 bg-stone-50 rounded border border-stone-200 text-xs text-stone-600 terms-section">
          <p className="font-bold mb-1">Terms & Conditions:</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Goods once sold will not be taken back.</li>
            <li>All disputes are subject to Amritsar jurisdiction.</li>
            <li>Please check goods at the time of delivery.</li>
            <li>E. & O.E. (Errors and Omissions Excepted)</li>
          </ol>
        </div>

        {/* Signature Section */}
        <div className="mt-8 pt-6 border-t-2 border-stone-300 signature-section">
          <div className="grid grid-cols-3 gap-8 text-sm text-stone-600">
            <div className="text-center">
              <p className="mb-12"><strong>Receiver's Signature</strong></p>
              <div className="border-t border-stone-400 pt-2">
                <p>Name: ___________________</p>
                <p className="mt-1">Date: ___________________</p>
              </div>
            </div>
            <div className="text-center">
              <p className="mb-12"><strong>Driver's Signature</strong></p>
              <div className="border-t border-stone-400 pt-2">
                <p>Name: ___________________</p>
                <p className="mt-1">Date: ___________________</p>
              </div>
            </div>
            <div className="text-center">
              <p className="mb-12"><strong>For S.P Singh and Sons</strong></p>
              <div className="border-t border-stone-400 pt-2">
                <p>Authorized Signatory</p>
                <p className="mt-1">Date: ___________________</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t border-stone-200 text-center">
          <p className="text-xs text-stone-500">
            This is a computer-generated challan from SPS Groups Inventory Management System.
          </p>
          <p className="text-xs text-stone-400 mt-1">
            Generated on {new Date().toLocaleString('en-IN')} • © {new Date().getFullYear()} S.P Singh and Sons
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrintChallan;
